import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Dialog } from '../../Dialog';
import { ImageUpload } from '../../ImageUpload/ImageUpload';
import { Heading } from '../../Heading';
import classes from './CreateArticleDialog.module.css';
import {
  ArticleDraftRequest,
  ArticleRequest,
  Label,
} from '../../../api/queries';
import { useGetLabelsQuery } from '../../../hooks/queries/useGetLabelsQuery/useGetLabelsQuery';
import { Paragraph } from '../../Paragraph';
import { Alert } from '../../Alert/Alert';
import { Button } from '../../Button';
import { usePublishArticleMutation } from '../../../hooks/mutations/usePublishArticleMutation';
import { useUploadBlob } from '../../../hooks/mutations/useUploadBlob';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDeleteBlobMutation } from '../../../hooks/mutations/useDeleteBlobMutation';
import { Spinner } from '../../Spinner';
import { ArticleUtils } from '../../../utils/ArticleUtils/ArticleUtils';
import { buildPublishPayload } from '../../../utils/CreateArticleDialogUtils/CreateArticleDialogUtils';
import { LabelsList } from '../LabelsList/LabelsList';

type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
type ArticleWithOptional = MakeOptional<
  ArticleDraftRequest,
  'coverImageUrl' | 'coverImageAlt' | 'labels'
>;

type CreateArticleDialogProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  updateDraftArticle: <K extends keyof ArticleRequest>(
    key: K,
    value: ArticleRequest[K],
  ) => void;
  article: ArticleWithOptional;
  setCoverImage: React.Dispatch<React.SetStateAction<File | null>>;
  coverImage: File | null;
  setSelectedLabels: React.Dispatch<React.SetStateAction<Array<Label>>>;
  selectedLabels: Array<Label>;
  setArticle: Dispatch<SetStateAction<ArticleDraftRequest>>;
};

export const CreateArticleDialog = ({
  isOpen,
  setIsOpen,
  updateDraftArticle,
  article,
  setCoverImage,
  coverImage,
  setSelectedLabels,
  selectedLabels,
  setArticle,
}: CreateArticleDialogProps) => {
  const navigate = useNavigate();
  useEffect(() => {
    setSelectedLabels(article.labels || []);
  }, [article.labels, setSelectedLabels]);
  const [imagePreview, setImagePreview] = useState<string>(
    article.coverImageUrl || '',
  );
  const isEditing = window.location.pathname.includes('edit');

  const {
    data: labels,
    isLoading: isLabelsLoading,
    error: labelsError,
  } = useGetLabelsQuery();

  const { mutate: publishArticleMutation, isPending: isPublishPending } =
    usePublishArticleMutation();
  const { mutate: uploadBlobMutation, isPending: isUploadPending } =
    useUploadBlob();
  const { mutate: deleteBlobMutation } = useDeleteBlobMutation();

  const onDiscardImage = () => {
    const splitImageUrl = article.coverImageUrl?.split('/').reverse();
    const blobName = splitImageUrl?.[0];

    if (article.coverImageUrl && blobName) {
      deleteBlobMutation(blobName, {
        onSuccess: () => {
          setCoverImage(null);
          setImagePreview('');
          updateDraftArticle('coverImageUrl', '');
          if (article.coverImageAlt) {
            updateDraftArticle('coverImageAlt', '');
          }
        },
      });
    }
  };

  const onPublishSuccess = () => {
    setArticle(ArticleUtils.resetArticle());
    navigate('/content-system/articles');
    toast.success(`Artikkelen er ${isEditing ? 'oppdatert' : 'opprettet'}.`, {
      position: 'bottom-right',
    });
  };

  const publishArticle = () => {
    if (!article.coverImageAlt?.trim() || !selectedLabels.length) {
      return;
    }

    if (article.coverImageUrl && !coverImage?.name) {
      publishArticleMutation(
        buildPublishPayload(
          {
            ...article,
            coverImageAlt: article.coverImageAlt ?? '',
            coverImageUrl: article.coverImageUrl ?? '',
            labels: selectedLabels,
          },
          selectedLabels,
          article.coverImageUrl,
        ),
        { onSuccess: onPublishSuccess },
      );
    }

    if (coverImage?.name) {
      uploadBlobMutation(coverImage, {
        onSuccess: ({ url }) => {
          publishArticleMutation(
            buildPublishPayload(
              {
                ...article,
                coverImageAlt: article.coverImageAlt ?? '',
                coverImageUrl: url,
                labels: selectedLabels,
              },
              selectedLabels,
              url,
            ),
            { onSuccess: onPublishSuccess },
          );
        },
      });
    }
  };

  if (labelsError) {
    return (
      <Dialog isOpen={isOpen} closeDialog={() => setIsOpen(false)}>
        <div className={classes.publishModal}>
          <div className={classes.publishModalContent}></div>
          <div className={classes.labelsError}>
            <Alert
              title={'Feil ved henting av kategorier'}
              variant="danger"
              size="sm"
              headingSize="h4"
              style={{ marginBottom: 0 }}
            >
              <Heading as="h5">{labelsError.name}</Heading>
              <Paragraph size="md">{labelsError.message}</Paragraph>
            </Alert>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog isOpen={isOpen} closeDialog={() => setIsOpen(false)}>
      <div className={classes.publishModal}>
        <div className={classes.publishModalContent}>
          <div className={classes.coverImageUpload}>
            <Heading as="h4">Forsidebilde</Heading>
            <ImageUpload
              setImageAltText={(imageAlt: string) =>
                updateDraftArticle('coverImageAlt', imageAlt)
              }
              imageAltText={article.coverImageAlt ?? ''}
              setImage={setCoverImage}
              existingImageUpload={article.coverImageUrl ?? ''}
              setImagePreview={setImagePreview}
              imagePreview={imagePreview}
            />
          </div>
          <div className={classes.labelsContent}>
            <Heading as="h4">Kategoriser artikkelen</Heading>
            {isLabelsLoading ? (
              <div>
                <Spinner color={'var(--primary)'} />
                <Paragraph size="md">Laster kategorier...</Paragraph>
              </div>
            ) : labels && labels.length > 0 ? (
              <LabelsList
                labels={labels || []}
                setSelectedLabels={setSelectedLabels}
                selectedLabels={selectedLabels}
              />
            ) : (
              <Paragraph size="md">Ingen kategorier opprettet.</Paragraph>
            )}
          </div>
        </div>

        <div className={classes.publishButtons}>
          <Button
            type="button"
            variant="primary"
            disabled={
              selectedLabels.length === 0 ||
              !imagePreview ||
              !article?.coverImageAlt?.trim() ||
              isPublishPending ||
              isUploadPending
            }
            onClick={publishArticle}
          >
            {isPublishPending || isUploadPending ? (
              <Spinner color="var(--ds-color-accent-base-contrast-default)" />
            ) : (
              'Publiser nå'
            )}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onDiscardImage}
            className={classes.discardButton}
          >
            Fjern bilde
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
